import {
  NJButton,
  NJFormItem,
  NJHeading,
  NJInlineMessage,
  NJMultiSelectItem,
  NJMultiSelectRoot,
  NJSelectItem,
  NJSelectRoot,
  NJSpinner,
  NJText,
} from "@engie-group/fluid-design-system-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { errorMessage } from "@/api/apiError";
import { BackButton } from "@/components/BackButton";
import {
  type FormErrors,
  isBlank,
  isTooLong,
  isValidUrl,
  lengthHint,
  tooLongMessage,
} from "@/components/form/validation";
import { MarkdownMessage } from "@/features/chat/MarkdownMessage";
import { SkillCompatibilityBrandIcon } from "@/features/skills/SkillCompatibilityBrandIcon";
import { SKILL_COMPATIBILITY_OPTIONS } from "@/features/skills/skillCompatibility";
import {
  SKILL_NAME_MAX_LENGTH,
  SKILL_SHORT_DESCRIPTION_MAX_LENGTH,
} from "@/features/skills/skillLimits";
import { useSkillCategories } from "@/features/skills/useSkills";
import type { AdminSkillUpdate } from "@/types";
import {
  useAdminSkill,
  useCreateAdminSkill,
  useUpdateAdminSkill,
} from "./useAdminSkills";

type SourceType = "ZIP" | "GITHUB";
type Tab = "general" | "categories" | "harnesses";
type FieldKey = "name" | "shortDescription" | "repositoryUrl" | "zipFile";

type FormState = {
  name: string;
  shortDescription: string;
  fullDescription: string;
  includedSkills: string[];
  compatibleHarnesses: string[];
  sourceType: SourceType;
  repositoryUrl: string;
  status: string;
};

const INITIAL_STATE: FormState = {
  name: "",
  shortDescription: "",
  fullDescription: "",
  includedSkills: [],
  compatibleHarnesses: [],
  sourceType: "ZIP",
  repositoryUrl: "",
  status: "ACTIVE",
};

const REQUIRED_MESSAGE = "This field is required.";

/** Shared admin skill create/edit page (US-12). Tabs: General / Categories / Compatible
 * harnesses, with field-level validation and a live markdown preview. */
export function AdminSkillFormPage() {
  const { slug } = useParams<{ slug: string }>();
  const isEdit = Boolean(slug);
  const navigate = useNavigate();

  const { data: skill, isLoading } = useAdminSkill(slug);
  const { data: categoryOptions } = useSkillCategories();
  const createSkill = useCreateAdminSkill();
  const updateSkill = useUpdateAdminSkill();

  const [tab, setTab] = useState<Tab>("general");
  const [form, setForm] = useState<FormState>(INITIAL_STATE);
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<FormErrors<FieldKey>>({});

  useEffect(() => {
    if (skill) {
      setForm({
        name: skill.name,
        shortDescription: skill.description ?? "",
        fullDescription: skill.fullDescription ?? "",
        includedSkills: skill.includedSkills ?? [],
        compatibleHarnesses: skill.compatibleHarnesses ?? [],
        sourceType: (skill.sourceType as SourceType) ?? "ZIP",
        repositoryUrl: skill.repositoryUrl ?? "",
        status: skill.status ?? "ACTIVE",
      });
    }
  }, [skill]);

  const categoryChoices = useMemo(
    () =>
      Array.from(
        new Set([...(categoryOptions ?? []), ...form.includedSkills]),
      ).sort((a, b) => a.localeCompare(b)),
    [categoryOptions, form.includedSkills],
  );

  const isGithub = form.sourceType === "GITHUB";

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  function validate(): FormErrors<FieldKey> {
    const next: FormErrors<FieldKey> = {};
    const fail = (field: FieldKey, message: string) => {
      next[field] = message;
    };
    if (isBlank(form.name)) fail("name", REQUIRED_MESSAGE);
    else if (isTooLong(form.name, SKILL_NAME_MAX_LENGTH))
      fail("name", tooLongMessage(form.name, SKILL_NAME_MAX_LENGTH));
    if (isBlank(form.shortDescription))
      fail("shortDescription", REQUIRED_MESSAGE);
    else if (
      isTooLong(form.shortDescription, SKILL_SHORT_DESCRIPTION_MAX_LENGTH)
    )
      fail(
        "shortDescription",
        tooLongMessage(
          form.shortDescription,
          SKILL_SHORT_DESCRIPTION_MAX_LENGTH,
        ),
      );
    if (isGithub) {
      if (isBlank(form.repositoryUrl)) {
        fail("repositoryUrl", REQUIRED_MESSAGE);
      } else if (!isValidUrl(form.repositoryUrl)) {
        fail("repositoryUrl", "Enter a valid http(s) URL.");
      }
    } else if (!isEdit && !zipFile) {
      fail("zipFile", "A ZIP file is required.");
    }
    return next;
  }

  async function handleSubmit() {
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setTab("general");
      return;
    }

    if (isEdit && slug) {
      const patch: AdminSkillUpdate = {
        name: form.name,
        shortDescription: form.shortDescription,
        fullDescription: form.fullDescription || null,
        includedSkills: form.includedSkills,
        compatibleHarnesses: form.compatibleHarnesses,
        repositoryUrl: isGithub ? form.repositoryUrl.trim() || null : null,
        status: form.status,
      };
      await updateSkill.mutateAsync({ slug, patch });
    } else {
      await createSkill.mutateAsync({
        name: form.name,
        shortDescription: form.shortDescription,
        fullDescription: form.fullDescription || undefined,
        includedSkills: form.includedSkills,
        compatibleHarnesses: form.compatibleHarnesses,
        sourceType: form.sourceType,
        repositoryUrl: isGithub
          ? form.repositoryUrl.trim() || undefined
          : undefined,
        zipFile: isGithub ? undefined : (zipFile ?? undefined),
      });
    }
    navigate("/admin/skills");
  }

  const mutation = isEdit ? updateSkill : createSkill;

  if (isEdit && isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <NJSpinner aria-label="Loading the skill" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <BackButton to="/admin/skills" label="Back to Skills" />
      <NJHeading as="h1" scale="lg">
        {isEdit ? `Edit skill — ${form.name}` : "Create a skill"}
      </NJHeading>

      <div className="flex gap-2 border-b border-nj-neutral-subtle-default">
        <TabButton active={tab === "general"} onClick={() => setTab("general")}>
          General
        </TabButton>
        <TabButton
          active={tab === "categories"}
          onClick={() => setTab("categories")}
        >
          Categories
        </TabButton>
        <TabButton
          active={tab === "harnesses"}
          onClick={() => setTab("harnesses")}
        >
          Compatible harnesses
        </TabButton>
      </div>

      {tab === "general" && (
        <div className="grid w-full gap-4 sm:grid-cols-2 sm:gap-x-6">
          <NJFormItem
            id="skill-name"
            label="Name"
            isRequired
            hasError={Boolean(errors.name)}
            subscriptMessage={errors.name}
            value={form.name}
            onChange={(e) => set("name", (e.target as HTMLInputElement).value)}
          />
          <NJFormItem
            id="skill-short-description"
            label="Short description"
            isRequired
            hasError={Boolean(errors.shortDescription)}
            subscriptMessage={
              errors.shortDescription ??
              lengthHint(
                form.shortDescription,
                SKILL_SHORT_DESCRIPTION_MAX_LENGTH,
              )
            }
            value={form.shortDescription}
            onChange={(e) =>
              set("shortDescription", (e.target as HTMLInputElement).value)
            }
          />

          {isEdit ? (
            <NJSelectRoot
              id="skill-status"
              label="Status"
              value={form.status}
              listNavigationLabel="Use the arrows and Enter to choose the status"
              buttonDefaultValueLabel="Choose a status"
              onChange={(value) => set("status", value ?? "ACTIVE")}
            >
              <NJSelectItem value="PENDING">PENDING</NJSelectItem>
              <NJSelectItem value="ACTIVE">ACTIVE</NJSelectItem>
              <NJSelectItem value="DISABLED">DISABLED</NJSelectItem>
            </NJSelectRoot>
          ) : (
            <NJSelectRoot
              id="skill-source-type"
              label="Source"
              value={form.sourceType}
              listNavigationLabel="Use the arrows and Enter to choose the source"
              buttonDefaultValueLabel="Choose a source"
              onChange={(value) => {
                const nextSource = (value ?? "ZIP") as SourceType;
                set("sourceType", nextSource);
                if (nextSource === "ZIP") set("repositoryUrl", "");
                setZipFile(null);
              }}
            >
              <NJSelectItem value="ZIP">ZIP package</NJSelectItem>
              <NJSelectItem value="GITHUB">GitHub repository</NJSelectItem>
            </NJSelectRoot>
          )}

          <div className="sm:col-span-2">
            {isGithub ? (
              <NJFormItem
                id="skill-repository-url"
                label="Repository URL"
                isRequired
                hasError={Boolean(errors.repositoryUrl)}
                subscriptMessage={errors.repositoryUrl}
                value={form.repositoryUrl}
                onChange={(e) =>
                  set("repositoryUrl", (e.target as HTMLInputElement).value)
                }
              />
            ) : isEdit ? (
              <NJText
                as="p"
                scale="sm"
                className="text-nj-neutral-secondary-default"
              >
                ZIP versions are managed from the skill detail page.
              </NJText>
            ) : (
              <label className="flex flex-col gap-1">
                <NJText as="span" scale="sm" className="font-nj-bold">
                  ZIP file *
                </NJText>
                <input
                  type="file"
                  accept=".zip"
                  onChange={(e) => setZipFile(e.target.files?.[0] ?? null)}
                />
                {errors.zipFile && (
                  <NJText as="span" scale="xs" variant="danger">
                    {errors.zipFile}
                  </NJText>
                )}
              </label>
            )}
          </div>

          <div className="sm:col-span-2">
            <NJFormItem
              id="skill-full-description"
              label="Full description (markdown)"
              isMultiline
              value={form.fullDescription}
              onChange={(e) =>
                set("fullDescription", (e.target as HTMLTextAreaElement).value)
              }
            />
          </div>
          <div className="sm:col-span-2">
            <PreviewBox markdown={form.fullDescription} />
          </div>
        </div>
      )}

      {tab === "categories" && (
        <NJMultiSelectRoot
          id="skill-categories"
          label="Categories"
          value={form.includedSkills}
          placeholder="Select categories"
          listNavigationLabel="Use arrow keys and Enter to toggle a category"
          maxTagsToDisplay={5}
          tagColor="brand"
          onChange={(nextValue) => set("includedSkills", nextValue)}
        >
          {categoryChoices.map((option) => (
            <NJMultiSelectItem key={option} value={option} label={option}>
              {option}
            </NJMultiSelectItem>
          ))}
        </NJMultiSelectRoot>
      )}

      {tab === "harnesses" && (
        <NJMultiSelectRoot
          id="skill-harnesses"
          label="Compatible with"
          value={form.compatibleHarnesses}
          placeholder="Select compatible harnesses"
          listNavigationLabel="Use arrow keys and Enter to toggle a harness"
          maxTagsToDisplay={5}
          tagColor="brand"
          onChange={(nextValue) => set("compatibleHarnesses", nextValue)}
        >
          {SKILL_COMPATIBILITY_OPTIONS.map((option) => (
            <NJMultiSelectItem key={option} value={option} label={option}>
              <span className="inline-flex items-center gap-2">
                <SkillCompatibilityBrandIcon value={option} />
                <span>{option}</span>
              </span>
            </NJMultiSelectItem>
          ))}
        </NJMultiSelectRoot>
      )}

      {mutation.isError && (
        <NJInlineMessage variant="error">
          {errorMessage(
            mutation.error,
            "Could not save this skill (the slug may already be taken).",
          )}
        </NJInlineMessage>
      )}

      <div className="flex gap-3">
        <NJButton
          label={isEdit ? "Save" : "Create skill"}
          disabled={mutation.isPending}
          onClick={() => void handleSubmit()}
        />
        <NJButton
          label="Cancel"
          variant="secondary"
          emphasis="subtle"
          onClick={() => navigate("/admin/skills")}
        />
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px border-b-2 px-4 py-2 text-sm font-nj-bold transition ${
        active
          ? "border-nj-brand-solid-default text-nj-brand-solid-default"
          : "border-transparent text-nj-neutral-secondary-default hover:text-nj-neutral-primary-default"
      }`}
    >
      {children}
    </button>
  );
}

function PreviewBox({ markdown }: { markdown: string }) {
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-nj-neutral-subtle-default bg-nj-neutral-secondary-default p-3">
      <NJText
        as="span"
        scale="xs"
        className="font-nj-bold uppercase tracking-wide text-nj-neutral-secondary-default"
      >
        Preview
      </NJText>
      {markdown.trim() ? (
        <MarkdownMessage content={markdown} />
      ) : (
        <NJText
          as="span"
          scale="sm"
          className="text-nj-neutral-tertiary-default"
        >
          No markdown preview yet.
        </NJText>
      )}
    </div>
  );
}
